import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  ChevronRight, Mail, Phone, MapPin, Send, MessageSquare, HelpCircle, Truck, Package,
  ShieldCheck, RefreshCcw, CreditCard, Globe2, Sparkles, Zap, Users, Briefcase, FileText,
  Trophy, Megaphone, ChevronDown, BookOpen, Video, TrendingUp, ScrollText, ExternalLink, Loader2,
  CheckCircle2, Newspaper,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = 'contact@respectfulmarkets.com';
const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY || '10000000-ffff-ffff-ffff-000000000001';

// =============================================================================
// Hero Component — reused across info pages
// =============================================================================
const InfoHero = ({ title, subtitle, breadcrumb, icon: Icon, noSeo = false }) => {
  const T = usePageT();
  return (
    <>
    {!noSeo && <SEO title={title} description={subtitle} url={typeof window !== 'undefined' ? window.location.origin + window.location.pathname : undefined} />}
    <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white py-16">
      <div className="container-main">
        <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
          <Link to="/" className="hover:text-white">{T('Accueil', 'Home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#E9C46A]">{breadcrumb}</span>
        </nav>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight flex items-center gap-3">
          {Icon && <Icon className="w-10 h-10 text-[#E9C46A]" />}
          {title}
        </h1>
        {subtitle && <p className="text-white/70 mt-3 max-w-2xl text-lg">{subtitle}</p>}
      </div>
    </div>
    </>
  );
};

// =============================================================================
// 1. À Propos — Standout pricing policy
// =============================================================================
export const AboutPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="about-page">
      <InfoHero
        title={T('Notre mission', 'Our mission')}
        subtitle={T(
          'Construire la marketplace européenne qui rémunère le mieux ses vendeurs — sans compromis sur la qualité du service.',
          'Building the European marketplace that pays its sellers the best — with no compromise on service quality.',
        )}
        breadcrumb={T('À propos', 'About us')}
        icon={Trophy}
      />

      <div className="container-main py-16">
        {/* Manifesto */}
        <section className="max-w-3xl mx-auto mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F4C5C] mb-6">
            {T('Pourquoi Respectful Markets existe', 'Why Respectful Markets exists')}
          </h2>
          <div className="space-y-4 text-[#0F4C5C]/90 leading-relaxed">
            <p>
              {T(
                'Les grandes marketplaces prennent jusqu\'à 15% sur chaque vente, imposent des frais cachés, des abonnements obligatoires et changent leurs règles du jour au lendemain. Pendant ce temps, les vendeurs français — artisans, PME, créateurs — peinent à dégager une marge décente.',
                'The biggest marketplaces take up to 15% on every sale, impose hidden fees, mandatory subscriptions and change their rules overnight. Meanwhile, French sellers — artisans, SMEs, creators — struggle to make a decent margin.',
              )}
            </p>
            <p>
              {T(
                'Respectful Markets a été conçu pour inverser cette logique : nous reversons le maximum aux vendeurs, dans la transparence, avec des outils dignes de 2026 (IA, multi-devises, expédition européenne).',
                'Respectful Markets was designed to flip this logic: we pay out the maximum to sellers, transparently, with 2026-grade tools (AI, multi-currency, European shipping).',
              )}
            </p>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="mb-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-bold text-[#0F4C5C] mb-3">
              {T('Comment nos prix se comparent', 'How our pricing compares')}
            </h2>
            <p className="text-[#0F4C5C]/70 max-w-2xl mx-auto">
              {T('Comparaison transparente avec les grandes marketplaces (commissions standard, hors promotions ponctuelles).',
                'Transparent comparison with the biggest marketplaces (standard commissions, excluding occasional promotions).')}
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-[#0F4C5C]/10 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-[#0F4C5C] text-white">
                <tr>
                  <th className="text-left p-4 font-semibold">{T('Plateforme', 'Platform')}</th>
                  <th className="text-center p-4 font-semibold">{T('Commission', 'Commission')}</th>
                  <th className="text-center p-4 font-semibold">{T('Abonnement', 'Subscription')}</th>
                  <th className="text-center p-4 font-semibold">{T('Frais cachés', 'Hidden fees')}</th>
                  <th className="text-center p-4 font-semibold">{T('Paiement vendeur', 'Vendor payout')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-emerald-50/50 border-b border-[#0F4C5C]/5">
                  <td className="p-4 font-bold text-emerald-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Respectful Markets (Pro)
                  </td>
                  <td className="p-4 text-center font-bold text-emerald-700">3%</td>
                  <td className="p-4 text-center font-semibold">25€/mois</td>
                  <td className="p-4 text-center text-emerald-700 font-semibold">0€</td>
                  <td className="p-4 text-center font-semibold">{T('Hebdomadaire', 'Weekly')}</td>
                </tr>
                <tr className="bg-emerald-50/30 border-b border-[#0F4C5C]/5">
                  <td className="p-4 font-bold text-emerald-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Respectful Markets (Starter)
                  </td>
                  <td className="p-4 text-center font-bold text-emerald-700">12%</td>
                  <td className="p-4 text-center font-semibold text-emerald-700">{T('Gratuit', 'Free')}</td>
                  <td className="p-4 text-center text-emerald-700 font-semibold">0€</td>
                  <td className="p-4 text-center font-semibold">{T('Hebdomadaire', 'Weekly')}</td>
                </tr>
                <tr className="border-b border-[#0F4C5C]/5">
                  <td className="p-4 text-[#0F4C5C]/70">Amazon Marketplace</td>
                  <td className="p-4 text-center">8% — 15%</td>
                  <td className="p-4 text-center">39€/mois (Pro)</td>
                  <td className="p-4 text-center text-rose-600">{T('Oui (FBA, pubs)', 'Yes (FBA, ads)')}</td>
                  <td className="p-4 text-center">14 {T('jours', 'days')}</td>
                </tr>
                <tr className="border-b border-[#0F4C5C]/5">
                  <td className="p-4 text-[#0F4C5C]/70">eBay</td>
                  <td className="p-4 text-center">10% — 12.9%</td>
                  <td className="p-4 text-center">{T('Optionnel', 'Optional')}</td>
                  <td className="p-4 text-center text-rose-600">{T('Frais d\'insertion', 'Listing fees')}</td>
                  <td className="p-4 text-center">{T('Variable', 'Variable')}</td>
                </tr>
                <tr className="border-b border-[#0F4C5C]/5">
                  <td className="p-4 text-[#0F4C5C]/70">Etsy</td>
                  <td className="p-4 text-center">6.5% + 0.18€/listing</td>
                  <td className="p-4 text-center">{T('Variable', 'Variable')}</td>
                  <td className="p-4 text-center text-rose-600">{T('Pub obligatoire 12%', 'Mandatory ads 12%')}</td>
                  <td className="p-4 text-center">3-7 {T('jours', 'days')}</td>
                </tr>
                <tr>
                  <td className="p-4 text-[#0F4C5C]/70">Cdiscount Pro</td>
                  <td className="p-4 text-center">6% — 17%</td>
                  <td className="p-4 text-center">39.99€/mois</td>
                  <td className="p-4 text-center text-rose-600">{T('Référencement payant', 'Paid listings')}</td>
                  <td className="p-4 text-center">30 {T('jours', 'days')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#0F4C5C]/50 mt-3 text-center">
            {T('Données indicatives publiées par chaque plateforme au 1er trimestre 2026.', 'Indicative data published by each platform Q1 2026.')}
          </p>
        </section>

        {/* Our principles */}
        <section className="mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F4C5C] text-center mb-10">
            {T('Nos engagements vendeurs', 'Our seller commitments')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, fr: ['97% reversé aux vendeurs Pro', 'Aucune commission ne dépasse 12%. Pour un panier de 100€, vous gardez minimum 88€.'], en: ['97% paid to Pro sellers', 'No commission exceeds 12%. On a 100€ cart, you keep at least 88€.'] },
              { icon: ShieldCheck, fr: ['Zéro frais caché', 'Pas de pubs obligatoires, pas de frais de listing, pas de pénalité de désinscription. Promis.'], en: ['Zero hidden fees', 'No mandatory ads, no listing fees, no exit penalty. Promised.'] },
              { icon: Zap, fr: ['Paiement hebdomadaire', 'Vos fonds sont reversés via Stripe Connect tous les lundis, sans condition de seuil.'], en: ['Weekly payout', 'Your funds are released via Stripe Connect every Monday, no threshold.'] },
              { icon: Globe2, fr: ['Vente européenne incluse', 'Visibilité automatique sur 7 langues et 8 devises sans frais supplémentaire.'], en: ['European reach included', 'Automatic visibility in 7 languages and 8 currencies at no extra cost.'] },
              { icon: TrendingUp, fr: ['Devis B2B intégrés', 'Acceptez les RFQ et convertissez en commande Stripe en un clic.'], en: ['Integrated B2B quotes', 'Accept RFQs and convert to Stripe orders in one click.'] },
              { icon: Users, fr: ['Support humain en français', 'Une équipe dédiée — pas de chatbot, pas de file d\'attente de 48h.'], en: ['Human French support', 'A dedicated team — no chatbot, no 48h queue.'] },
            ].map((it, idx) => (
              <Card key={idx} className="border-[#0F4C5C]/10 hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-[#E9C46A]/20 flex items-center justify-center mb-3">
                    <it.icon className="w-6 h-6 text-[#0F4C5C]" />
                  </div>
                  <CardTitle className="text-base font-bold text-[#0F4C5C]">{T(it.fr[0], it.en[0])}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#0F4C5C]/70 leading-relaxed">{T(it.fr[1], it.en[1])}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-[#0F4C5C] to-[#2A9D8F] rounded-3xl p-8 md:p-12 text-white text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            {T('Prêt à vendre dans le respect ?', 'Ready to sell with respect?')}
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            {T('Rejoignez plus de 10 000 vendeurs qui ont choisi des commissions justes et un service humain.',
              'Join 10,000+ sellers who chose fair commissions and human service.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/become-seller">
              <Button className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-semibold px-8 py-6 rounded-full" data-testid="about-cta-become-seller">
                {T('Devenir vendeur', 'Become a seller')}
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 font-semibold px-8 py-6 rounded-full">
                {T('Voir nos tarifs', 'View pricing')}
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

// =============================================================================
// 2. Contact — avec formulaire qui envoie sur contact@respectfulmarkets.com
// =============================================================================
export const ContactPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  const [form, setForm] = useState({ name: '', email: '', subject: '', topic: 'general', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(T('Veuillez remplir tous les champs obligatoires', 'Please fill all required fields'));
      return;
    }
    if (!captchaToken) {
      toast.error(T('Veuillez valider le captcha avant d\'envoyer', 'Please complete the captcha first'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, captcha_token: captchaToken });
      setSent(true);
      toast.success(T('Message envoyé. Nous vous répondrons sous 24h.', 'Message sent. We\'ll reply within 24h.'));
      setForm({ name: '', email: '', subject: '', topic: 'general', message: '' });
      setCaptchaToken(null);
      if (captchaRef.current) captchaRef.current.resetCaptcha();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur lors de l\'envoi. Réessayez ou écrivez-nous directement.', 'Failed to send. Try again or email us directly.'));
      if (captchaRef.current) captchaRef.current.resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="contact-page">
      <InfoHero
        title={T('Nous contacter', 'Contact us')}
        subtitle={T(
          'Une question, un partenariat, un retour ? Notre équipe répond sous 24h ouvrées.',
          'A question, a partnership, feedback? Our team replies within 24 business hours.',
        )}
        breadcrumb={T('Nous contacter', 'Contact us')}
        icon={MessageSquare}
      />

      <div className="container-main py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
          {/* Contact info */}
          <div className="lg:col-span-1 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-[#0F4C5C] mb-4">{T('Coordonnées', 'Get in touch')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0F4C5C]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-[#0F4C5C]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#0F4C5C]/60 uppercase tracking-wide">Email</p>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0F4C5C] font-semibold hover:text-[#2A9D8F] transition-colors" data-testid="contact-email">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0F4C5C]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#0F4C5C]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#0F4C5C]/60 uppercase tracking-wide">{T('Téléphone', 'Phone')}</p>
                    <p className="text-[#0F4C5C] font-semibold">+33 1 23 45 67 89</p>
                    <p className="text-xs text-[#0F4C5C]/60">{T('Lun-Ven 9h-18h', 'Mon-Fri 9am-6pm')}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0F4C5C]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#0F4C5C]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#0F4C5C]/60 uppercase tracking-wide">{T('Adresse', 'Address')}</p>
                    <p className="text-[#0F4C5C] font-semibold">Paris, France</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-[#0F4C5C]/10 rounded-xl p-5">
              <h4 className="font-bold text-[#0F4C5C] mb-2 flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                {T('Besoin d\'aide rapide ?', 'Need quick help?')}
              </h4>
              <p className="text-sm text-[#0F4C5C]/70 mb-3">
                {T('Notre Centre d\'aide répond aux 90% des questions en moins de 30 secondes.', 'Our Help Center answers 90% of questions in under 30 seconds.')}
              </p>
              <Link to="/help">
                <Button variant="outline" size="sm" className="w-full">
                  {T('Visiter le Centre d\'aide', 'Visit Help Center')}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="border-[#0F4C5C]/10 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl text-[#0F4C5C]">
                  {T('Envoyez-nous un message', 'Send us a message')}
                </CardTitle>
                <CardDescription>
                  {T('Tous les champs marqués * sont obligatoires.', 'All fields marked * are required.')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sent ? (
                  <div className="text-center py-10" data-testid="contact-success">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0F4C5C] mb-2">
                      {T('Message bien reçu !', 'Message received!')}
                    </h3>
                    <p className="text-sm text-[#0F4C5C]/70 max-w-md mx-auto mb-6">
                      {T('Nous vous répondrons sous 24h ouvrées à l\'adresse que vous avez indiquée.', 'We\'ll reply within 24 business hours to the email you provided.')}
                    </p>
                    <Button onClick={() => setSent(false)} variant="outline" data-testid="contact-send-another">
                      {T('Envoyer un autre message', 'Send another message')}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ct-name">{T('Nom complet', 'Full name')} *</Label>
                        <Input id="ct-name" data-testid="contact-name"
                          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          required className="mt-1.5 h-11" />
                      </div>
                      <div>
                        <Label htmlFor="ct-email">Email *</Label>
                        <Input id="ct-email" type="email" data-testid="contact-email-input"
                          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          required className="mt-1.5 h-11" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="ct-topic">{T('Sujet', 'Topic')}</Label>
                      <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                        <SelectTrigger id="ct-topic" data-testid="contact-topic" className="mt-1.5 h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{T('Question générale', 'General question')}</SelectItem>
                          <SelectItem value="order">{T('Commande / Livraison', 'Order / Delivery')}</SelectItem>
                          <SelectItem value="vendor">{T('Devenir vendeur', 'Become a seller')}</SelectItem>
                          <SelectItem value="b2b">{T('Devis B2B', 'B2B quote')}</SelectItem>
                          <SelectItem value="partnership">{T('Partenariat / Presse', 'Partnership / Press')}</SelectItem>
                          <SelectItem value="legal">{T('Juridique / RGPD', 'Legal / GDPR')}</SelectItem>
                          <SelectItem value="other">{T('Autre', 'Other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="ct-subject">{T('Objet (optionnel)', 'Subject (optional)')}</Label>
                      <Input id="ct-subject" data-testid="contact-subject"
                        value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        placeholder={T('Résumé en quelques mots', 'Summary in a few words')}
                        className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="ct-message">Message *</Label>
                      <Textarea id="ct-message" data-testid="contact-message"
                        value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        required rows={6}
                        placeholder={T('Décrivez votre demande en détail...', 'Describe your request in detail...')}
                        className="mt-1.5 resize-y" />
                    </div>
                    <p className="text-xs text-[#0F4C5C]/60">
                      {T(
                        'En soumettant ce formulaire, vous acceptez que vos données soient traitées conformément à notre',
                        'By submitting this form, you agree your data will be processed per our',
                      )}{' '}
                      <Link to="/privacy" className="underline">{T('politique de confidentialité', 'privacy policy')}</Link>.
                    </p>
                    <div className="flex justify-center sm:justify-start" data-testid="contact-hcaptcha-container">
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={HCAPTCHA_SITEKEY}
                        onVerify={(token) => setCaptchaToken(token)}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                        languageOverride={language}
                        theme="light"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit" disabled={submitting || !captchaToken} className="btn-primary" data-testid="contact-submit">
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        {T('Envoyer le message', 'Send message')}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 3. Help Center — Centre d'Aide
// =============================================================================
export const HelpCenterPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  const sections = [
    {
      icon: Package,
      title: T('Commandes & livraison', 'Orders & delivery'),
      desc: T('Suivre votre colis, modifier une adresse, livraison internationale', 'Track your parcel, change address, international shipping'),
      links: [
        { l: T('Suivi de commande', 'Track order'), to: '/track' },
        { l: T('Modes de livraison', 'Shipping methods'), to: '/faq#shipping' },
        { l: T('Retards / problèmes', 'Delays / issues'), to: '/faq#issues' },
      ],
    },
    {
      icon: RefreshCcw,
      title: T('Retours & remboursements', 'Returns & refunds'),
      desc: T('Politique 30 jours, démarrer un retour, remboursement Stripe', '30-day policy, start a return, Stripe refund'),
      links: [
        { l: T('Politique de retour', 'Return policy'), to: '/returns' },
        { l: T('Comment retourner ?', 'How to return?'), to: '/faq#return' },
      ],
    },
    {
      icon: CreditCard,
      title: T('Paiement & sécurité', 'Payment & security'),
      desc: T('Cartes acceptées, paiement fractionné, conformité PCI-DSS', 'Accepted cards, split payment, PCI-DSS compliance'),
      links: [
        { l: T('Moyens de paiement', 'Payment methods'), to: '/faq#payment' },
        { l: T('Sécurité Stripe', 'Stripe security'), to: '/faq#security' },
      ],
    },
    {
      icon: ShieldCheck,
      title: T('Compte & RGPD', 'Account & GDPR'),
      desc: T('Modifier votre profil, exporter vos données, supprimer le compte', 'Edit profile, export data, delete account'),
      links: [
        { l: T('Gérer mon compte', 'Manage my account'), to: '/account' },
        { l: T('Exporter / Supprimer mes données', 'Export / Delete my data'), to: '/account?tab=privacy' },
      ],
    },
    {
      icon: Briefcase,
      title: T('Vendre sur Respectful Markets', 'Sell on Respectful Markets'),
      desc: T('S\'inscrire, gérer ses produits, paiement Stripe Connect', 'Register, manage products, Stripe Connect payouts'),
      links: [
        { l: T('Devenir vendeur', 'Become a seller'), to: '/become-seller' },
        { l: T('Tarifs vendeurs', 'Vendor pricing'), to: '/pricing' },
        { l: T('Ressources vendeur', 'Seller resources'), to: '/seller-resources' },
      ],
    },
    {
      icon: FileText,
      title: T('B2B & devis', 'B2B & quotes'),
      desc: T('Achats professionnels, demande de devis, conditions B2B', 'Pro purchases, request a quote, B2B terms'),
      links: [
        { l: T('Demander un devis', 'Request a quote'), to: '/shop' },
        { l: T('Mes devis', 'My quotes'), to: '/quotes' },
        { l: T('FAQ B2B', 'B2B FAQ'), to: '/faq#b2b' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="help-center-page">
      <InfoHero
        title={T('Centre d\'aide', 'Help Center')}
        subtitle={T(
          'Tout ce qu\'il vous faut pour acheter, vendre et profiter de Respectful Markets.',
          'Everything you need to buy, sell and enjoy Respectful Markets.',
        )}
        breadcrumb={T('Centre d\'aide', 'Help Center')}
        icon={HelpCircle}
      />

      <div className="container-main py-12">
        {/* Search hint */}
        <div className="bg-white border border-[#0F4C5C]/10 rounded-2xl p-6 mb-10 flex flex-col md:flex-row items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#E9C46A]/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-7 h-7 text-[#0F4C5C]" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="font-bold text-[#0F4C5C]">
              {T('Vous ne trouvez pas la réponse ?', 'Can\'t find your answer?')}
            </p>
            <p className="text-sm text-[#0F4C5C]/70">
              {T('Notre équipe humaine répond sous 24h ouvrées (pas de chatbot).', 'Our human team replies within 24 business hours (no chatbot).')}
            </p>
          </div>
          <Link to="/contact">
            <Button className="btn-primary" data-testid="help-contact-btn">
              <MessageSquare className="w-4 h-4 mr-2" />
              {T('Nous contacter', 'Contact us')}
            </Button>
          </Link>
        </div>

        {/* Topic grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((s, idx) => (
            <Card key={idx} className="border-[#0F4C5C]/10 hover:shadow-md transition-shadow" data-testid={`help-section-${idx}`}>
              <CardHeader>
                <div className="w-12 h-12 rounded-xl bg-[#0F4C5C]/10 flex items-center justify-center mb-3">
                  <s.icon className="w-6 h-6 text-[#0F4C5C]" />
                </div>
                <CardTitle className="text-base text-[#0F4C5C]">{s.title}</CardTitle>
                <CardDescription className="text-sm">{s.desc}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {s.links.map((lk) => (
                    <li key={lk.to + lk.l}>
                      <Link to={lk.to} className="text-sm text-[#2A9D8F] hover:text-[#0F4C5C] font-medium flex items-center gap-1 transition-colors">
                        <ChevronRight className="w-3 h-3" />
                        {lk.l}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 4. FAQ Page
// =============================================================================
export const FAQPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  const categories = [
    {
      id: 'orders',
      title: T('Commandes', 'Orders'),
      faqs: [
        { q: T('Comment passer une commande ?', 'How to place an order?'),
          a: T('Ajoutez les produits au panier, cliquez sur "Commander", remplissez votre adresse de livraison et procédez au paiement sécurisé via Stripe.',
               'Add products to cart, click "Checkout", enter your shipping address and pay securely via Stripe.') },
        { q: T('Puis-je modifier ma commande après paiement ?', 'Can I modify my order after payment?'),
          a: T('Tant que la commande n\'est pas marquée "Expédiée" par le vendeur, contactez-le via la messagerie pour modification. Sinon, utilisez la procédure de retour.',
               'As long as the order isn\'t marked "Shipped" by the vendor, contact them via messaging. Otherwise, use the return process.') },
        { q: T('Comment suivre ma commande ?', 'How to track my order?'),
          a: T('Allez dans "Mes commandes" → cliquez sur la commande → un lien de suivi transporteur apparaît dès l\'expédition.',
               'Go to "My Orders" → click the order → a carrier tracking link appears once shipped.') },
      ],
    },
    {
      id: 'shipping',
      title: T('Livraison', 'Shipping'),
      faqs: [
        { q: T('Quels sont les délais ?', 'What are the delivery times?'),
          a: T('Standard 3-5j, Express 1-2j, Point relais 4-6j. Livraison internationale UE 5-8j.',
               'Standard 3-5 days, Express 1-2 days, Pickup point 4-6 days. EU international 5-8 days.') },
        { q: T('Livrez-vous à l\'étranger ?', 'Do you ship internationally?'),
          a: T('Oui : France, Belgique, Suisse, Allemagne, Italie, Espagne, Pays-Bas, Portugal, Royaume-Uni et bientôt États-Unis et Canada.',
               'Yes: France, Belgium, Switzerland, Germany, Italy, Spain, Netherlands, Portugal, UK and soon US/Canada.') },
        { q: T('Frais de port ?', 'Shipping fees?'),
          a: T('Gratuit dès 50€ d\'achat en France. Sinon, calcul automatique selon le poids, le pays et le transporteur.',
               'Free from 50€ in France. Otherwise, automatic calculation based on weight, country and carrier.') },
      ],
    },
    {
      id: 'return',
      title: T('Retours', 'Returns'),
      faqs: [
        { q: T('Puis-je retourner un produit ?', 'Can I return a product?'),
          a: T('Oui, vous disposez de 30 jours après réception pour tout retour, sans avoir à justifier votre décision.',
               'Yes, you have 30 days after receipt for any return, without needing to justify your decision.') },
        { q: T('Combien de temps pour le remboursement ?', 'How long for refund?'),
          a: T('Une fois le produit reçu et inspecté par le vendeur (sous 5 jours), Stripe restitue les fonds en 5-10 jours ouvrés sur votre carte.',
               'Once received and inspected by the vendor (within 5 days), Stripe refunds within 5-10 business days to your card.') },
      ],
    },
    {
      id: 'payment',
      title: T('Paiement', 'Payment'),
      faqs: [
        { q: T('Quels modes de paiement acceptez-vous ?', 'What payment methods do you accept?'),
          a: T('Cartes Visa, Mastercard, American Express via Stripe Checkout. PayPal en option pour certains vendeurs.',
               'Visa, Mastercard, American Express cards via Stripe Checkout. PayPal optional for some vendors.') },
        { q: T('Mes données de paiement sont-elles sécurisées ?', 'Is my payment data secure?'),
          a: T('100%. Stripe est certifié PCI-DSS niveau 1. Nous ne voyons ni ne stockons jamais votre numéro de carte.',
               '100%. Stripe is PCI-DSS Level 1 certified. We never see or store your card number.') },
      ],
    },
    {
      id: 'security',
      title: T('Sécurité', 'Security'),
      faqs: [
        { q: T('Comment protégez-vous mon compte ?', 'How do you protect my account?'),
          a: T('Mots de passe hashés (bcrypt), JWT signés, HTTPS partout, conformité RGPD complète.',
               'Hashed passwords (bcrypt), signed JWTs, HTTPS everywhere, full GDPR compliance.') },
      ],
    },
    {
      id: 'b2b',
      title: T('B2B & Devis', 'B2B & Quotes'),
      faqs: [
        { q: T('Comment demander un devis ?', 'How to request a quote?'),
          a: T('Sur chaque fiche produit, cliquez sur "Demander un devis" en bas. Remplissez le formulaire avec votre société et SIRET.',
               'On each product page, click "Request a quote" at the bottom. Fill the form with your company and tax ID.') },
        { q: T('Quel est le délai de réponse vendeur ?', 'How fast do vendors respond?'),
          a: T('La majorité des vendeurs répondent sous 24h. Vous recevez une notification email + un statut "Devis reçu" dans /quotes.',
               'Most vendors reply within 24h. You receive an email notification + "Quote received" status in /quotes.') },
        { q: T('Puis-je payer un devis en ligne ?', 'Can I pay a quote online?'),
          a: T('Oui ! Dans /quotes, cliquez sur "Accepter et payer" pour générer une session Stripe avec les prix négociés.',
               'Yes! In /quotes, click "Accept and pay" to generate a Stripe session with the negotiated prices.') },
      ],
    },
    {
      id: 'issues',
      title: T('Problèmes', 'Issues'),
      faqs: [
        { q: T('Mon colis n\'arrive pas', 'My parcel hasn\'t arrived'),
          a: T('Vérifiez le suivi dans "Mes commandes". Si le délai estimé est dépassé de 3 jours, contactez le vendeur via la messagerie. Sinon ouvrez un ticket via /contact.',
               'Check tracking in "My Orders". If estimated time is exceeded by 3 days, contact the vendor via messaging. Otherwise open a ticket via /contact.') },
        { q: T('J\'ai reçu un produit endommagé', 'I received a damaged product'),
          a: T('Prenez des photos sous 48h. Ouvrez un retour depuis la commande en sélectionnant "Produit endommagé" — remboursement intégral garanti.',
               'Take photos within 48h. Open a return from the order selecting "Damaged product" — full refund guaranteed.') },
      ],
    },
  ];

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: categories.flatMap((cat) => cat.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    }))),
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="faq-page">
      <SEO
        title="FAQ"
        description={T(
          'Réponses aux questions fréquentes : commandes, livraison, retours, paiement, sécurité et devis B2B sur Respectful Markets.',
          'Answers to frequent questions: orders, shipping, returns, payment, security and B2B quotes on Respectful Markets.'
        )}
        url={`${window.location.origin}/faq`}
        jsonLd={faqLd}
      />
      <InfoHero
        noSeo
        title="FAQ"
        subtitle={T('Réponses aux questions les plus fréquentes — mises à jour chaque semaine.', 'Answers to the most frequent questions — updated weekly.')}
        breadcrumb="FAQ"
        icon={HelpCircle}
      />

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto space-y-8">
          {categories.map((cat) => (
            <div key={cat.id} id={cat.id}>
              <h2 className="text-2xl font-bold text-[#0F4C5C] mb-4">{cat.title}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {cat.faqs.map((it, i) => (
                  <AccordionItem
                    key={i}
                    value={`${cat.id}-${i}`}
                    className="bg-white border border-[#0F4C5C]/10 rounded-xl px-4 data-[state=open]:border-[#2A9D8F]"
                    data-testid={`faq-item-${cat.id}-${i}`}
                  >
                    <AccordionTrigger className="text-left text-[#0F4C5C] font-semibold py-4 hover:no-underline">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#0F4C5C]/80 pb-4 leading-relaxed">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          <div className="bg-gradient-to-br from-[#0F4C5C] to-[#2A9D8F] text-white rounded-2xl p-8 text-center mt-10">
            <h3 className="text-xl font-bold mb-2">{T('Votre question n\'est pas là ?', 'Your question isn\'t here?')}</h3>
            <p className="text-white/80 mb-4">{T('Notre équipe répond sous 24h ouvrées.', 'Our team replies within 24 business hours.')}</p>
            <Link to="/contact">
              <Button className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-semibold">
                {T('Nous contacter', 'Contact us')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 5. Order Tracking — Suivi de commande
// =============================================================================
export const TrackOrderPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [trackId, setTrackId] = useState('');
  const T = usePageT();

  const handleTrack = (e) => {
    e.preventDefault();
    if (trackId.trim()) navigate(`/track/${trackId.trim()}`);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="track-order-page">
      <InfoHero
        title={T('Suivi de commande', 'Track your order')}
        subtitle={T('Vérifiez le statut de votre colis en temps réel.', 'Check your parcel status in real time.')}
        breadcrumb={T('Suivi de commande', 'Track order')}
        icon={Truck}
      />

      <div className="container-main py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="border-[#0F4C5C]/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C]">
                {T('Suivre un colis', 'Track a parcel')}
              </CardTitle>
              <CardDescription>
                {T('Saisissez le numéro de commande présent dans votre email de confirmation.',
                   'Enter the order number from your confirmation email.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
                <Input
                  value={trackId}
                  onChange={(e) => setTrackId(e.target.value)}
                  placeholder={T('Numéro de commande (ex: 388bd54b-…)', 'Order number (e.g. 388bd54b-…)')}
                  className="flex-1"
                  data-testid="track-order-input"
                />
                <Button type="submit" className="btn-primary" disabled={!trackId.trim()} data-testid="track-order-submit">
                  <Truck className="w-4 h-4 mr-2" />
                  {T('Suivre', 'Track')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-[#0F4C5C]/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C]">
                {T('Accéder à vos commandes', 'Access your orders')}
              </CardTitle>
              <CardDescription>
                {T(
                  'Le suivi de chaque commande est disponible dans votre espace personnel après connexion.',
                  'Tracking for each order is available in your personal area after login.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#FBF8F3] rounded-xl p-5 border border-[#0F4C5C]/10">
                <h3 className="font-bold text-[#0F4C5C] mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {T('3 étapes simples', '3 simple steps')}
                </h3>
                <ol className="space-y-2 text-sm text-[#0F4C5C]/80">
                  <li><strong>1.</strong> {T('Connectez-vous à votre compte', 'Log into your account')}</li>
                  <li><strong>2.</strong> {T('Cliquez sur "Mes commandes" dans le menu', 'Click "My Orders" in the menu')}</li>
                  <li><strong>3.</strong> {T('Sélectionnez la commande pour voir le suivi détaillé', 'Select the order to view detailed tracking')}</li>
                </ol>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/orders" className="flex-1">
                  <Button className="btn-primary w-full" data-testid="track-go-orders">
                    <Package className="w-4 h-4 mr-2" />
                    {T('Voir mes commandes', 'View my orders')}
                  </Button>
                </Link>
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full">
                    {T('Me connecter', 'Log in')}
                  </Button>
                </Link>
              </div>

              <div className="text-sm text-[#0F4C5C]/70 bg-amber-50 border-l-4 border-amber-400 rounded p-3">
                <p>
                  <strong>{T('Astuce :', 'Tip:')}</strong>{' '}
                  {T(
                    'Un email avec le numéro de tracking vous est envoyé automatiquement dès qu\'un vendeur expédie votre colis.',
                    'An email with the tracking number is automatically sent as soon as a vendor ships your parcel.',
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// 6. Careers, Press, Seller Center, Seller Resources — lightweight content pages
// =============================================================================
const SimpleContentPage = ({ title, breadcrumb, icon, sections, ctaText, ctaTo, testid }) => (
  <div className="min-h-screen bg-[#FBF8F3]" data-testid={testid}>
    <InfoHero title={title} breadcrumb={breadcrumb} icon={icon} />
    <div className="container-main py-12">
      <div className="max-w-3xl mx-auto space-y-6">
        {sections.map((s, idx) => (
          <Card key={idx} className="border-[#0F4C5C]/10">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{s.title}</CardTitle>
            </CardHeader>
            <CardContent className="text-[#0F4C5C]/80 leading-relaxed space-y-3">
              {s.content}
            </CardContent>
          </Card>
        ))}
        {ctaText && ctaTo && (
          <div className="text-center pt-4">
            <Link to={ctaTo}>
              <Button className="btn-primary px-8 py-6 rounded-full">{ctaText}</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const CareersPage = () => {
  const { language } = useLanguage();
  const T = usePageT();
  return (
    <SimpleContentPage
      title={T('Rejoignez-nous', 'Join us')}
      breadcrumb={T('Carrières', 'Careers')}
      icon={Users}
      testid="careers-page"
      ctaText={T('Postuler spontanément', 'Submit spontaneous application')}
      ctaTo="/contact"
      sections={[
        {
          title: T('Construire la marketplace responsable de demain', 'Building tomorrow\'s responsible marketplace'),
          content: (
            <>
              <p>{T(
                'Respectful Markets est une startup française qui défie les pratiques des géants en remettant les vendeurs et les acheteurs au centre. Nous recrutons des passionné·e·s prêt·e·s à bâtir un commerce plus juste.',
                'Respectful Markets is a French startup challenging giants by putting sellers and buyers back at the center. We hire passionate people ready to build fairer commerce.',
              )}</p>
              <p>{T('Nous offrons : télétravail flexible, équité (BSPCE), tickets-restaurant Swile, mutuelle Alan, congés illimités encadrés.',
                    'We offer: flexible remote, equity (BSPCE), Swile lunch tickets, Alan health insurance, supervised unlimited PTO.')}</p>
            </>
          ),
        },
        {
          title: T('Postes actuellement ouverts', 'Current open positions'),
          content: (
            <ul className="list-disc list-inside space-y-2">
              <li>{T('Développeur·euse Full-Stack (React / FastAPI) — Paris ou remote France', 'Full-Stack Developer (React / FastAPI) — Paris or remote France')}</li>
              <li>{T('Customer Success Manager bilingue FR/DE', 'Bilingual FR/DE Customer Success Manager')}</li>
              <li>{T('Designer Produit Senior (Figma + design system)', 'Senior Product Designer (Figma + design system)')}</li>
              <li>{T('Vendor Onboarding Specialist (mi-temps possible)', 'Vendor Onboarding Specialist (part-time possible)')}</li>
            </ul>
          ),
        },
        {
          title: T('Notre processus', 'Our process'),
          content: (
            <ol className="list-decimal list-inside space-y-1">
              <li>{T('Échange de 30min avec notre People Lead', '30min chat with our People Lead')}</li>
              <li>{T('Étude de cas (1-2h sur projet réel, payée si plus de 2h)', 'Case study (1-2h on real project, paid if over 2h)')}</li>
              <li>{T('Rencontre équipe + alignement culture', 'Team meet + culture alignment')}</li>
              <li>{T('Offre sous 48h', 'Offer within 48h')}</li>
            </ol>
          ),
        },
      ]}
    />
  );
};

export const PressPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  // App screenshots & product visuals for press use
  const APP_SCREENSHOTS = [
    { url: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?auto=format&fit=crop&w=1600&q=85', captionFr: 'Tableau de bord vendeur', captionEn: 'Vendor dashboard' },
    { url: 'https://images.unsplash.com/photo-1556742111-a301076d9d18?auto=format&fit=crop&w=1600&q=85', captionFr: 'Page produit avec variantes', captionEn: 'Product page with variants' },
    { url: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1600&q=85', captionFr: 'Devis B2B intégré', captionEn: 'Integrated B2B quote' },
    { url: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=85', captionFr: 'Programme partenaire', captionEn: 'Partner program' },
    { url: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1600&q=85', captionFr: 'Analytics vendeur', captionEn: 'Vendor analytics' },
    { url: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=1600&q=85', captionFr: 'Multi-devises et multilingue', captionEn: 'Multi-currency & multilingual' },
  ];

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="press-page">
      <InfoHero
        title={T('Espace presse', 'Press room')}
        breadcrumb={T('Presse', 'Press')}
        icon={Megaphone}
      />
      <div className="container-main py-12">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* About */}
          <Card className="border-[#0F4C5C]/10">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{T('À propos de Respectful Markets', 'About Respectful Markets')}</CardTitle>
            </CardHeader>
            <CardContent className="text-[#0F4C5C]/80 leading-relaxed">
              <p>{T(
                'Respectful Markets est la marketplace française qui rémunère le mieux ses vendeurs (3% de commission Pro), avec une expérience moderne (devis B2B intégrés, multi-devises, programme d\'affiliation) et un engagement RGPD strict. Fondée en 2025, basée à Paris.',
                'Respectful Markets is the French marketplace that pays its sellers the best (3% Pro commission), with a modern experience (integrated B2B quotes, multi-currency, affiliate program) and strict GDPR commitment. Founded in 2025, based in Paris.',
              )}</p>
            </CardContent>
          </Card>

          {/* Press kit */}
          <Card className="border-[#0F4C5C]/10">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{T('Kit presse', 'Press kit')}</CardTitle>
              <CardDescription>{T('Téléchargez nos ressources officielles.', 'Download our official resources.')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li>
                  <a href={`${API}/press/logo.svg`} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] font-medium hover:underline inline-flex items-center gap-2" data-testid="press-logo-link">
                    📦 {T('Logo officiel (SVG vectoriel)', 'Official logo (vector SVG)')}
                  </a>
                </li>
                <li>
                  <a href={`${API}/press/key-figures.pdf`} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] font-medium hover:underline inline-flex items-center gap-2" data-testid="press-figures-link">
                    📊 {T('Chiffres clés 2026 (PDF)', 'Key figures 2026 (PDF)')}
                  </a>
                </li>
                <li>
                  <a href={`${API}/press/founders-bio.pdf`} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] font-medium hover:underline inline-flex items-center gap-2" data-testid="press-bio-link">
                    🎤 {T('Bio fondateurs courte + longue (PDF)', 'Founders bio short + long (PDF)')}
                  </a>
                </li>
                <li>
                  <a href={`${API}/press/product-visuals.zip`} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] font-medium hover:underline inline-flex items-center gap-2" data-testid="press-visuals-link">
                    🖼️ {T('Visuels produit haute définition (ZIP)', 'High-definition product visuals (ZIP)')}
                  </a>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* App screenshots gallery */}
          <Card className="border-[#0F4C5C]/10" data-testid="press-screenshots-gallery">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{T('Galerie — Captures d\'écran de l\'application', 'Gallery — App screenshots')}</CardTitle>
              <CardDescription>
                {T('Visuels en haute résolution libres d\'utilisation pour la presse. Crédit photo : Respectful Markets.',
                   'High-resolution visuals free to use for press. Photo credit: Respectful Markets.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {APP_SCREENSHOTS.map((img, i) => (
                  <a
                    key={i}
                    href={img.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                    data-testid={`press-screenshot-${i}`}
                  >
                    <div className="relative rounded-xl overflow-hidden border border-[#0F4C5C]/10 aspect-[4/3]">
                      <img src={img.url} alt={T(img.captionFr, img.captionEn)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                        <span className="text-white text-sm font-medium">{T(img.captionFr, img.captionEn)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-[#0F4C5C]/70 mt-2 text-center">{T(img.captionFr, img.captionEn)}</p>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent releases */}
          <Card className="border-[#0F4C5C]/10">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{T('Communiqués récents', 'Recent press releases')}</CardTitle>
              <CardDescription>{T('Toutes nos annonces et actualités.', 'All our announcements and news.')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/press/releases">
                <Button variant="outline" className="w-full" data-testid="press-releases-link">
                  <Newspaper className="w-4 h-4 mr-2" />
                  {T('Voir tous les communiqués de presse', 'View all press releases')}
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Interview requests */}
          <Card className="border-[#0F4C5C]/10">
            <CardHeader>
              <CardTitle className="text-[#0F4C5C] text-xl">{T('Demandes d\'interviews', 'Interview requests')}</CardTitle>
            </CardHeader>
            <CardContent className="text-[#0F4C5C]/80">
              <p>{T(
                'Pour toute demande d\'interview, citation ou collaboration éditoriale, écrivez-nous à press@respectfulmarkets.com — réponse garantie sous 24h ouvrées.',
                'For interview requests, quotes or editorial collaboration, email press@respectfulmarkets.com — guaranteed reply within 24 business hours.',
              )}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const SellerCenterPage = () => {
  const { language } = useLanguage();
  const T = usePageT();
  return (
    <SimpleContentPage
      title={T('Espace Vendeur', 'Seller Center')}
      breadcrumb={T('Espace Vendeur', 'Seller Center')}
      icon={Briefcase}
      testid="seller-center-page"
      ctaText={T('Accéder à mon tableau de bord', 'Go to my dashboard')}
      ctaTo="/vendor"
      sections={[
        {
          title: T('Tout pour piloter votre activité', 'Everything to run your business'),
          content: (
            <>
              <p>{T('Votre tableau de bord vendeur centralise :', 'Your vendor dashboard centralizes:')}</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>{T('Produits', 'Products')}</strong> — {T('ajout, édition, variantes, stock', 'add, edit, variants, stock')}</li>
                <li><strong>{T('Commandes', 'Orders')}</strong> — {T('expédition, tracking, factures', 'shipping, tracking, invoices')}</li>
                <li><strong>{T('Devis B2B', 'B2B Quotes')}</strong> — {T('réception, réponse, conversion Stripe', 'receive, respond, Stripe conversion')}</li>
                <li><strong>{T('Analytics', 'Analytics')}</strong> — {T('CA, top produits, conversion', 'revenue, top products, conversion')}</li>
                <li><strong>{T('Programme partenaire', 'Affiliate program')}</strong> — {T('générez des liens, gagnez 5-12% sur chaque vente', 'generate links, earn 5-12% per sale')}</li>
              </ul>
            </>
          ),
        },
        {
          title: T('Vous n\'êtes pas encore vendeur ?', 'Not a seller yet?'),
          content: (
            <p>{T(
              'Découvrez nos plans Starter (gratuit, 12% commission) et Pro (25€/mois, 3% commission) sur la',
              'Discover our Starter (free, 12% commission) and Pro (25€/month, 3% commission) plans on the',
            )} <Link to="/pricing" className="text-[#2A9D8F] font-semibold underline">{T('page Tarifs', 'Pricing page')}</Link>.</p>
          ),
        },
      ]}
    />
  );
};

export const SellerResourcesPage = () => {
  const { language } = useLanguage();
  const T = usePageT();
  return (
    <SimpleContentPage
      title={T('Ressources vendeur', 'Seller resources')}
      breadcrumb={T('Ressources vendeur', 'Seller resources')}
      icon={BookOpen}
      testid="seller-resources-page"
      ctaText={T('Voir le tableau de bord', 'Go to dashboard')}
      ctaTo="/vendor"
      sections={[
        {
          title: T('Guides pour démarrer', 'Getting started guides'),
          content: (
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <ScrollText className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0F4C5C]">{T('Guide complet KYC & SIRET', 'Complete KYC & business ID guide')}</p>
                  <p className="text-sm">{T('Comment valider votre compte vendeur en moins de 24h.', 'How to validate your seller account in under 24h.')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Video className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0F4C5C]">{T('Tutoriels vidéo (8 modules)', 'Video tutorials (8 modules)')}</p>
                  <p className="text-sm">{T('Ajouter un produit, gérer le stock, répondre à un devis, expédier, etc.', 'Add a product, manage stock, respond to a quote, ship, etc.')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0F4C5C]">{T('Optimiser vos fiches produit', 'Optimize your product listings')}</p>
                  <p className="text-sm">{T('Photos, titres, mots-clés : nos meilleures pratiques.', 'Photos, titles, keywords: our best practices.')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0F4C5C]">{T('Devenir affilié — guide du parrainage', 'Become an affiliate — referral guide')}</p>
                  <p className="text-sm">{T('Comment générer des liens et toucher 5 à 12% sur chaque vente.', 'How to generate links and earn 5-12% per sale.')}</p>
                </div>
              </li>
            </ul>
          ),
        },
        {
          title: T('Conditions financières', 'Financial terms'),
          content: (
            <ul className="list-disc list-inside space-y-2">
              <li>{T('Commission Pro : 3% par vente', 'Pro commission: 3% per sale')}</li>
              <li>{T('Commission Starter : 12% par vente (sans abonnement)', 'Starter commission: 12% per sale (no subscription)')}</li>
              <li>{T('Reversement : tous les lundis via Stripe Connect, dès 1€', 'Payout: every Monday via Stripe Connect, from 1€')}</li>
              <li>{T('Aucun frais d\'inscription, aucun frais de listing, aucune pénalité de désinscription', 'No signup fees, no listing fees, no exit penalty')}</li>
            </ul>
          ),
        },
        {
          title: T('Support dédié vendeur', 'Dedicated seller support'),
          content: (
            <p>{T(
              'Email prioritaire vendor-support@respectfulmarkets.com (réponse < 12h ouvrées), accès Slack communautaire, webinaires mensuels avec l\'équipe produit.',
              'Priority email vendor-support@respectfulmarkets.com (reply < 12 business hours), community Slack access, monthly webinars with the product team.',
            )}</p>
          ),
        },
      ]}
    />
  );
};
